import { CalendarClock, Check, Coins, type LucideIcon } from "lucide-react";
import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/Card";
import { useTotals } from "@/queries/useTotals";
import { useOwnerDisbursements } from "@/queries/financials/useOwnerDisbursements";
import { currencyFormat } from "@/util/formats";
import {
  formatNextCreditDate,
  groupEligibleDisbursementsBySydneyQuarter,
} from "@/util/sydneyQuarter";
import { selectTotal } from "@/util/totals";
import { formatAvailableCreditAmount } from "./creditMetrics";

type MetricCardData = {
  label: string;
  value: string;
  subtitle: string;
  icon: LucideIcon;
  iconClasses: string;
  valueClassName: string;
};

export default function MetricCardsRow() {
  const { data: totals, isPending, isError } = useTotals();
  const { data: disbursements, isLoading: isDisbursementsLoading } =
    useOwnerDisbursements();

  const hasTotalsError = isError && !totals;
  const totalsUnavailable = isPending || hasTotalsError;
  const totalCredits = selectTotal("Income", "Forever", totals)?.value;
  const lastQuarterCredits = selectTotal("Income", "LastQuarter", totals)?.value;
  // const thisQuarterCredits = selectTotal("Income", "ThisQuarter", totals)?.value;

  const eligibleQuarterTotals = useMemo(() => {
    return groupEligibleDisbursementsBySydneyQuarter(disbursements ?? []);
  }, [disbursements]);

  const latestDisbursedQuarter =
    eligibleQuarterTotals.length > 0
      ? eligibleQuarterTotals[eligibleQuarterTotals.length - 1]
      : undefined;

  const lastPaidAmount =
    lastQuarterCredits ?? latestDisbursedQuarter?.amount;

  const lastQuarterSubtitle = latestDisbursedQuarter
    ? `${latestDisbursedQuarter.quarter.dateRange} ${latestDisbursedQuarter.quarter.year}`
    : totalsUnavailable || isDisbursementsLoading
      ? "Paid by SolarCloud"
      : "No disbursements yet";

  const formatCredits = (value: number | null | undefined) =>
    formatAvailableCreditAmount(value, (amount) =>
      currencyFormat(amount, {
        fractionalDigits: 2,
        excludeCurrencyId: true,
      })
    );

  const cards: MetricCardData[] = [
    {
      label: "TOTAL SOLARCLOUD CREDITS",
      value: totalsUnavailable ? "—" : formatCredits(totalCredits),
      subtitle: "Accumulated",
      icon: Coins,
      iconClasses: "border-[#E9A837]/50 bg-[#2E220B] text-[#E9A837]",
      valueClassName: "text-[#E9A837]",
    },
    {
      label: "LAST QUARTER (ACTUAL)",
      value:
        lastPaidAmount != null
          ? formatCredits(lastPaidAmount)
          : totalsUnavailable || isDisbursementsLoading
            ? "—"
            : "—",
      subtitle: lastQuarterSubtitle,
      icon: Check,
      iconClasses: "border-[#E9A837]/50 bg-[#2E220B] text-[#E9A837]",
      valueClassName: "text-[#E9A837]",
    },
    // Temporarily hidden per john's request:
    // {
    //   label: "THIS QUARTER (ESTIMATE)",
    //   value: totalsUnavailable ? "—" : formatCredits(thisQuarterCredits),
    //   subtitle: "In progress",
    //   icon: CalendarCheck,
    //   iconClasses: "border-[#5E8E1E]/50 bg-[#1E330A] text-[#5E8E1E]",
    //   valueClassName: "text-[#5E8E1E]",
    // },
    {
      label: "NEXT CREDIT DATE",
      value: formatNextCreditDate(),
      subtitle: "Quarterly cadence",
      icon: CalendarClock,
      iconClasses: "border-[#E9A837]/50 bg-[#2E220B] text-[#E9A837]",
      valueClassName: "text-[#E9A837]",
    },
  ];

  return (
    <section aria-label="Credit summary" aria-busy={isPending}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {cards.map((card) => {
          const Icon = card.icon;

          return (
            <Card
              key={card.label}
              className="h-full rounded-[20px] border-[#232836] bg-[#12151C] py-0 text-[#D0D0D0] shadow-none"
            >
              <CardContent className="flex min-h-[108px] items-center gap-3 px-4 py-4">
                <div
                  className={`flex size-10 shrink-0 items-center justify-center rounded-full border ${card.iconClasses}`}
                >
                  <Icon aria-hidden="true" className="size-[18px]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[0.65rem] font-semibold uppercase tracking-[0.08em] text-[#E5E7EB]">
                    {card.label}
                  </p>
                  <p
                    className={`mt-1 truncate text-lg font-bold ${card.valueClassName}`}
                  >
                    {card.value}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-[#D1D5DB]">
                    {card.subtitle}
                  </p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {isPending && (
        <p className="sr-only" role="status">
          Loading credit totals
        </p>
      )}
      {hasTotalsError && (
        <p className="mt-3 text-sm text-red-400" role="alert">
          Credit totals could not be loaded.
        </p>
      )}
    </section>
  );
}
